/**
 * Field State Evaluator — single pure function that resolves ALL field UI state.
 *
 * Three-Lane Architecture, Lane 1: Declarative State Derivation.
 *
 * Precedence (highest wins):
 *   1. reactions[] overrides
 *   2. when clause (or legacy conditional* fallback)
 *   3. modeOverrides / blockOverrides
 *   4. permissionKey
 *   5. static defaults
 *
 * This function is:
 *   - Pure (no hooks, no side effects)
 *   - Testable (accepts all deps as arguments)
 *   - Used by: FormFieldAdapter, ConditionalGridItem, validationComposer
 */

import type { FormFieldMapping, FieldWhen, SelectOption, FieldReaction } from '../types/fieldMapping';
import type { Condition } from '../types/conditions';
import type { ModeOverrides } from '../types/schema';
import type { BlockOverrides } from '../types/block';
import { evaluateCondition, type ConditionContext } from '../evaluators/conditionEvaluator';
import type { ReactionOverrides } from './reactionProcessor';
import { getConditionalOverrides } from './modeResolver';
import { ActionRegistry } from '@packages/table-builder/registry/ActionRegistry';

/** Adapters that render their own read-only/disabled presentation instead of the generic label-value path. */
const ADAPTERS_WITH_OWN_READONLY: ReadonlySet<string> = new Set([
  'file-upload',
  'html-preview',
  'template-richtext',
]);

export interface ResolvedFieldState {
  visible: boolean;
  required: boolean;
  disabled: boolean;
  readOnly: boolean;
  label: string;
  placeholder: string;
  adapterHint: string;
  /** Active synchronous input formatter. Receives (value, prevValue) for deletion-direction detection. */
  formatter?: (value: string, prevValue: string) => string;
  options?: SelectOption[];
  minDate?: string;
  maxDate?: string;
  clearOnHide: boolean;
  /**
   * When true, this field renders through its adapter even in view/read-only mode
   * (with `disabled={true}`) rather than falling through to the generic label-value path.
   * Set automatically for adapters that handle their own read-only presentation (e.g. file-upload).
   */
  rendersOwnReadOnly: boolean;
}

export interface FieldStateContext {
  formValues: Record<string, unknown>;
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  isGlobalReadOnly: boolean;
  modeOverrides?: ModeOverrides;
  blockOverrides?: BlockOverrides;
  reactionOverrides: ReactionOverrides;
  namePrefix?: string;
}

export function evaluateFieldState(
  field: FormFieldMapping,
  ctx: FieldStateContext,
): ResolvedFieldState {
  const fieldName = ctx.namePrefix ? `${ctx.namePrefix}.${field.name}` : field.name;
  const fieldValue = getNestedValue(ctx.formValues, fieldName);

  const conditionCtx: ConditionContext = {
    fieldValue,
    values: ctx.formValues,
    meta: ctx.meta,
    getOptionProp: ctx.getOptionProp,
  };

  const when = resolveWhenClause(field);
  const fieldSuffix = field.name.split('.').pop() ?? field.name;
  const conditionalOverrides = getConditionalOverrides(
    fieldSuffix,
    ctx.modeOverrides,
    ctx.blockOverrides,
  );

  // --- VISIBLE ---
  let visible = true;
  if (field.hidden) {
    visible = false;
  } else if (ctx.reactionOverrides.visible !== undefined) {
    visible = ctx.reactionOverrides.visible;
  } else if (when.visible) {
    visible = evaluateCondition(when.visible, conditionCtx);
  } else if (conditionalOverrides.hidden) {
    visible = !evaluateCondition(conditionalOverrides.hidden, conditionCtx);
  }

  // Check mode/block hidden override (static boolean)
  if (visible) {
    const modeFieldOverride = ctx.modeOverrides?.fields?.[fieldSuffix];
    if (modeFieldOverride?.hidden === true) visible = false;
    const blockFieldOverride = ctx.blockOverrides?.fields?.[fieldSuffix];
    if (blockFieldOverride?.hidden === true) visible = false;
    if (ctx.modeOverrides?.fields?.[field.name]?.hidden === true) visible = false;
    if (ctx.blockOverrides?.fields?.[field.name]?.hidden === true) visible = false;
  }

  // --- PERMISSION ---
  const hasPermission = resolvePermission(field, ctx.meta);

  // --- REQUIRED ---
  let required = false;
  if (!hasPermission) {
    required = false;
  } else if (ctx.reactionOverrides.required !== undefined) {
    required = ctx.reactionOverrides.required;
  } else if (when.required) {
    required = evaluateCondition(when.required, conditionCtx);
  } else {
    required = field.required ?? false;
  }

  // Check mode/block required override
  const modeFieldOverride = ctx.modeOverrides?.fields?.[fieldSuffix]
    ?? ctx.modeOverrides?.fields?.[field.name];
  if (modeFieldOverride?.required === true) required = true;
  if (modeFieldOverride?.required === false) required = false;
  const blockFieldOverride = ctx.blockOverrides?.fields?.[fieldSuffix]
    ?? ctx.blockOverrides?.fields?.[field.name];
  if (blockFieldOverride?.required === true) required = true;
  if (blockFieldOverride?.required === false) required = false;
  if (conditionalOverrides.required) {
    required = evaluateCondition(conditionalOverrides.required, conditionCtx);
  }

  // --- DISABLED ---
  let disabled = false;
  if (!hasPermission) {
    disabled = true;
  } else if (ctx.reactionOverrides.disabled !== undefined) {
    disabled = ctx.reactionOverrides.disabled;
  } else if (when.disabled !== undefined) {
    disabled = typeof when.disabled === 'boolean'
      ? when.disabled
      : evaluateCondition(when.disabled, conditionCtx);
  }

  // Apply mode/block disabled (static boolean)
  if (ctx.modeOverrides?.disabled === true) disabled = true;
  if (modeFieldOverride?.disabled === true) disabled = true;
  if (ctx.blockOverrides?.disabled === true) disabled = true;
  if (blockFieldOverride?.disabled === true) disabled = true;
  if (conditionalOverrides.disabled) {
    disabled = disabled || evaluateCondition(conditionalOverrides.disabled, conditionCtx);
  }

  // --- READ-ONLY ---
  let readOnly = ctx.isGlobalReadOnly;
  if (ctx.reactionOverrides.readOnly !== undefined) {
    readOnly = readOnly || ctx.reactionOverrides.readOnly;
  } else if (when.readOnly !== undefined) {
    const fieldReadOnly = typeof when.readOnly === 'boolean'
      ? when.readOnly
      : evaluateCondition(when.readOnly, conditionCtx);
    readOnly = readOnly || fieldReadOnly;
  }

  if (ctx.modeOverrides?.readOnly === true) readOnly = true;
  if (modeFieldOverride?.readOnly === true) readOnly = true;
  if (ctx.blockOverrides?.readOnly === true) readOnly = true;
  if (blockFieldOverride?.readOnly === true) readOnly = true;
  if (conditionalOverrides.readOnly) {
    readOnly = readOnly || evaluateCondition(conditionalOverrides.readOnly, conditionCtx);
  }

  // --- ADAPTER HINT ---
  let adapterHint = field.overrideAdapterHint ?? field.def.adapterHint ?? 'text';
  const adapterHintWhen = when.adapterHint;
  if (adapterHintWhen) {
    const matches = evaluateCondition(adapterHintWhen.condition, conditionCtx);
    adapterHint = matches ? adapterHintWhen.trueHint : adapterHintWhen.falseHint;
  }

  // --- FORMATTER ---
  let formatter: ((value: string, prevValue: string) => string) | undefined;
  const formatterWhen = when.formatter;
  if (formatterWhen) {
    const matches = evaluateCondition(formatterWhen.condition, conditionCtx);
    formatter = matches ? formatterWhen.trueFormatter : formatterWhen.falseFormatter;
  }

  // --- LABEL / PLACEHOLDER ---
  const label = ctx.reactionOverrides.label ?? field.overrideLabel ?? field.def.label;
  const placeholder = ctx.reactionOverrides.placeholder ?? field.overridePlaceholder ?? field.def.placeholder ?? '';

  // --- OPTIONS ---
  const options = ctx.reactionOverrides.options ?? field.options ?? undefined;

  // --- DATE CONSTRAINTS ---
  const minDate = ctx.reactionOverrides.minDate;
  const maxDate = ctx.reactionOverrides.maxDate;

  // --- CLEAR-ON-HIDE ---
  // On edit mode, default to preserving hidden field values (server-owned data).
  // Schema authors can still opt in to clearing via `clearOnHideWhen: true`.
  const isEditMode = ctx.meta?.mode === 'edit' || ctx.meta?.mode === 'view';
  const clearOnHide = when.clearOnHide !== undefined ? when.clearOnHide : !isEditMode;

  return {
    visible,
    required,
    disabled: disabled || readOnly,
    readOnly,
    label,
    placeholder,
    adapterHint,
    formatter,
    options,
    minDate,
    maxDate,
    clearOnHide,
    rendersOwnReadOnly: ADAPTERS_WITH_OWN_READONLY.has(adapterHint),
  };
}

/**
 * Resolves the effective `when` clause by merging new `when` with legacy conditional* props.
 * `when` takes priority over legacy if both are provided.
 */
function resolveWhenClause(field: FormFieldMapping): FieldWhen {
  const when = field.when ?? {};

  return {
    visible: when.visible ?? field.conditionalVisible,
    required: when.required ?? field.conditionalRequired,
    disabled: when.disabled ?? field.conditionalDisabled,
    readOnly: when.readOnly,
    adapterHint: when.adapterHint ?? field.conditionalAdapterHint,
    formatter: when.formatter,
    clearOnHide: when.clearOnHide ?? field.clearOnHide,
  };
}

function resolvePermission(field: FormFieldMapping, meta: Record<string, unknown>): boolean {
  if (!field.permissions || Object.keys(field.permissions).length === 0) return true;
  const mode = (meta.mode as string | undefined) ?? '*';
  // Exact mode match takes priority over catch-all
  const actionId = field.permissions[mode] ?? field.permissions['*'];
  if (!actionId) return true; // mode not listed and no catch-all → no restriction
  const checker = meta.checkPermission as ((key: string) => boolean) | undefined;
  if (checker) return checker(actionId);
  return ActionRegistry.isActionAvailable(actionId);
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

/**
 * Checks for conflicting declarations between `when` and `reactions` in dev mode.
 * Returns an array of warning messages (empty in production or when no conflicts).
 */
export function detectFieldStateConflicts(field: FormFieldMapping): string[] {
  if (process.env.NODE_ENV === 'production') return [];

  const warnings: string[] = [];
  const when = field.when;
  const reactions = field.reactions;

  if (!when || !reactions || reactions.length === 0) return warnings;

  const reactionSets = new Set(reactions.map((r) => r.set));

  if (when.visible && reactionSets.has('visible')) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.visible\` and \`reactions[{set:'visible'}]\` defined. reactions take priority. Remove \`when.visible\` to avoid confusion.`,
    );
  }
  if (when.required && reactionSets.has('required')) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.required\` and \`reactions[{set:'required'}]\` defined. reactions take priority. Remove \`when.required\` to avoid confusion.`,
    );
  }
  if (when.disabled && reactionSets.has('disabled')) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.disabled\` and \`reactions[{set:'disabled'}]\` defined. reactions take priority. Remove \`when.disabled\` to avoid confusion.`,
    );
  }
  if (when.readOnly && reactionSets.has('readOnly')) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.readOnly\` and \`reactions[{set:'readOnly'}]\` defined. reactions take priority. Remove \`when.readOnly\` to avoid confusion.`,
    );
  }

  // Check for legacy + when overlap (using both systems)
  if (field.conditionalVisible && when.visible) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.visible\` and deprecated \`conditionalVisible\` defined. \`when.visible\` wins.`,
    );
  }
  if (field.conditionalRequired && when.required) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.required\` and deprecated \`conditionalRequired\` defined. \`when.required\` wins.`,
    );
  }
  if (field.conditionalDisabled && when.disabled) {
    warnings.push(
      `[form-engine] Field "${field.name}": both \`when.disabled\` and deprecated \`conditionalDisabled\` defined. \`when.disabled\` wins.`,
    );
  }

  return warnings;
}

/**
 * Lightweight evaluator for submit-time validation.
 * Only resolves visible + required (what validationComposer needs).
 */
export function evaluateFieldValidationState(
  field: FormFieldMapping,
  formValues: Record<string, unknown>,
  meta: Record<string, unknown>,
  getOptionProp: (field: string, prop: string) => unknown,
): { visible: boolean; required: boolean } {
  const fieldValue = getNestedValue(formValues, field.name);

  const conditionCtx: ConditionContext = {
    fieldValue,
    values: formValues,
    meta,
    getOptionProp,
  };

  const when = resolveWhenClause(field);

  let visible = true;
  if (field.hidden) {
    visible = false;
  } else if (when.visible) {
    visible = evaluateCondition(when.visible, conditionCtx);
  }

  if (!visible) return { visible: false, required: false };

  const hasPermission = resolvePermission(field, meta);
  if (!hasPermission) return { visible: true, required: false };

  let required = false;
  if (when.required) {
    required = evaluateCondition(when.required, conditionCtx);
  } else {
    required = field.required ?? false;
  }

  return { visible, required };
}
